import { FC, useEffect, useMemo, useRef, useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import {
  Box,
  Button,
  Checkbox,
  Text,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  VStack,
  HStack,
  Spinner,
  Center,
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Skeleton,
  Stack,
  useToast,
  FormControl,
  FormLabel,
  Input as ChakraInput,
} from '@chakra-ui/react';
import { UserModulesService, User, ModuleInfo } from './user-service';
import { MdPeople } from 'react-icons/md';
import { Icon } from '@chakra-ui/react';
import { Table, TableRowActions, Panel, PageHeading } from "@bemap/ui";
import { useTranslation } from "react-i18next";
import { DEFAULT_USER_MODULES, useModules } from "@bemap/ui";

const UsersPage: FC = () => {
  const toast = useToast()
  const { t } = useTranslation()
  const modules = useModules()

  // STATES
  const cancelRef = useRef<HTMLButtonElement>(null)
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [modalType, setModalType] = useState<"edit" | "new" | "delete">();
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userModules, setUserModules] = useState<ModuleInfo[]>([]);
  const [updateData, setUpdateData] = useState<string>("");
  const [isDeleting, setIsDeleting] = useState(false);
  const initialUser: User = {
    id: 0,
    name: '',
    email: '',
    active: true,
    modules: { ...DEFAULT_USER_MODULES }
  }

  const columns = useMemo<ColumnDef<User, any>[]>(
    () => [
      {
        accessorFn: (row: User) => row.name,
        id: "Nome",
        header: t('admin.users_table_name'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: User) => row.email,
        id: "Email",
        header: t('admin.users_table_email'),
        cell: (info: any) => info.getValue(),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: User) => row.active,
        id: "Status",
        header: t('general.status'),
        cell: (info: any) => (
          <Badge colorScheme={info.getValue() ? 'green' : 'red'}>
            {info.getValue() ? 'Active' : 'Inactive'}
          </Badge>
        ),
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: User) => row.modules,
        id: "Modules",
        header: t('admin.users_table_modules'),
        cell: (info: any) => {
          const userModules = info.getValue() as Record<string, boolean> || {};
          const activeModules = Object.entries(userModules)
            .filter(([_, hasAccess]) => hasAccess)
            .map(([moduleId]) => moduleId);

          if (activeModules.length === 0) {
            return (
              <Text fontSize="sm" color="gray.500">
                No modules
              </Text>
            );
          }

          return (
            <HStack spacing={1} flexWrap="wrap">
              {activeModules.map((moduleId) => {
                const moduleInfo = modules[moduleId as keyof typeof modules];
                if (!moduleInfo) return null;

                return (
                  <Badge
                    key={moduleId}
                    colorScheme="blue"
                    variant="subtle"
                    fontSize="xs"
                    px={2}
                    py={1}
                  >
                    {moduleInfo.label}
                  </Badge>
                );
              })}
            </HStack>
          );
        },
        footer: (props: any) => props.column.id,
      },
      {
        accessorFn: (row: User) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions'),
        cell: (row: any) => {
          return (
            <TableRowActions
              editAction={() => editUser(row.getValue())}
              deleteAction={() => openDeleteDialog(row.getValue())}
            />
          );
        },
      },
    ],
    [t, modules]
  );

  useEffect(() => {
    loadUsers();
  }, [updateData]);

  function closeModal() {
    onClose()
    setSelectedUser(initialUser)
  }

  async function loadUsers() {
    setIsLoading(true);
    try {
      const { message } = await UserModulesService.getUsers();
      setUsers(message);
      setIsLoading(false);
    } catch (error) {
      setUsers([]);
      setIsLoading(false);
    }
  }

  async function editUser(user: User) {
    try {
      setSelectedUser(user);

      // Initialize module list for the user
      const moduleList = Object.entries(modules).map(([moduleId, moduleInfo]) => ({
        id: moduleId,
        label: moduleInfo.label,
        description: moduleInfo.description,
        hasAccess: user.modules?.[moduleId] ?? DEFAULT_USER_MODULES[moduleId as keyof typeof DEFAULT_USER_MODULES] ?? false,
      }));

      setUserModules(moduleList);
      setModalType("edit");
      onOpen();
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  }

  async function openNewUserModal() {
    setSelectedUser(initialUser);

    // Initialize module list with default modules
    const moduleList = Object.entries(modules).map(([moduleId, moduleInfo]) => ({
      id: moduleId,
      label: moduleInfo.label,
      description: moduleInfo.description,
      hasAccess: DEFAULT_USER_MODULES[moduleId as keyof typeof DEFAULT_USER_MODULES] ?? false,
    }));

    setUserModules(moduleList);
    setModalType("new");
    onOpen();
  }

  async function openDeleteDialog(user: User) {
    try {
      setSelectedUser(user);
      setModalType("delete");
      onOpen();
    } catch (error) {
      setIsLoading(false);
    }
  }

  async function createNewUser() {
    if (!selectedUser) return;

    setIsLoading(true);
    setIsSaving(true);
    toast({
      title: t('general.saving'),
      status: 'loading',
      isClosable: true,
    })
    try {
      // Get modules from the form
      const updatedModules = userModules.reduce((acc, module) => {
        acc[module.id] = module.hasAccess;
        return acc;
      }, {} as Record<string, boolean>);

      // Create user with selected modules
      const userWithModules = {
        ...selectedUser,
        modules: updatedModules
      };

      await UserModulesService.createUser(userWithModules);
      setIsLoading(false)
      setIsSaving(false)
      closeModal()
      setUpdateData(new Date().toLocaleString())
    } catch (error) {
      setIsLoading(false);
      setIsSaving(false);
    }
    finally {
      toast.closeAll();
    }
  }

  const handleUpdateUser = async (payload: User) => {
    setIsSaving(true)
    toast({
      title: t('general.loading'),
      status: 'loading',
      isClosable: true,
    })
    try {
      // Update user modules first
      const updatedModules = userModules.reduce((acc, module) => {
        acc[module.id] = module.hasAccess;
        return acc;
      }, {} as Record<string, boolean>);

      await UserModulesService.updateUserModules(payload.id, updatedModules);

      // Then update user data
      const userWithModules = {
        ...payload,
        modules: updatedModules
      };

      await UserModulesService.updateUser(userWithModules);

      setUsers(prev =>
        prev.map(user =>
          user.id === payload.id
            ? { ...user, ...userWithModules }
            : user
        )
      );

      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error: any) {
      console.error(error.message);
    } finally {
      toast.closeAll();
      setIsSaving(false)
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;

    try {
      setIsDeleting(true);
      toast({
        title: t('general.deleting'),
        status: 'loading',
        isClosable: true,
      })
      await UserModulesService.deleteUser(selectedUser.id);
      setUpdateData(new Date().toLocaleString())
      closeModal()
    } catch (error) {
      toast({
        title: t('general.error'),
        status: 'error',
        duration: 9000,
        isClosable: true,
      })
    } finally {
      setIsDeleting(false);
      toast.closeAll();
    }
  };

  async function handleChange(event: any) {
    if (!selectedUser) return;

    setSelectedUser({
      ...selectedUser,
      [event.target.name]: event.target.value,
    });
  }



  const handleModuleToggle = (moduleId: string) => {
    setUserModules(prev =>
      prev.map(module =>
        module.id === moduleId
          ? { ...module, hasAccess: !module.hasAccess }
          : module
      )
    );
  };



  function RenderModal() {
    switch (modalType) {
      case "edit":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>{t('admin.users_edit_title')}</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={6}>
                  {/* User Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <FormControl isRequired>
                      <FormLabel>{t('admin.users_name_field')}</FormLabel>
                      <ChakraInput
                        name="name"
                        placeholder={t('general.placeholder_name')}
                        value={selectedUser?.name || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>E-mail</FormLabel>
                      <ChakraInput
                        name="email"
                        type="email"
                        placeholder={t('general.placeholder_email')}
                        value={selectedUser?.email || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                  </VStack>

                  {/* Module Management */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      {t('admin.manage_modules_button')}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('admin.select_modules')}
                    </Text>

                    {userModules.map((module) => (
                      <Box
                        key={module.id}
                        p={4}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{module.label}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {module.description}
                            </Text>
                          </VStack>
                          <Checkbox
                            isChecked={module.hasAccess}
                            onChange={() => handleModuleToggle(module.id)}
                            colorScheme="blue"
                          />
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button
                  colorScheme={"blue"}
                  onClick={() => handleUpdateUser(selectedUser!)}
                  isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case "new":
        return (
          <Modal isOpen={isOpen} onClose={closeModal}>
            <ModalOverlay />
            <ModalContent>
              <ModalHeader>Novo Usuário</ModalHeader>
              <ModalCloseButton />
              <ModalBody>
                <VStack spacing={6}>
                  {/* User Information */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      User Information
                    </Text>
                    <FormControl isRequired>
                      <FormLabel>{t('admin.users_name_field')}</FormLabel>
                      <ChakraInput
                        name="name"
                        placeholder={t('general.placeholder_name')}
                        value={selectedUser?.name || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                    <FormControl isRequired>
                      <FormLabel>E-mail</FormLabel>
                      <ChakraInput
                        name="email"
                        type="email"
                        placeholder={t('general.placeholder_email')}
                        value={selectedUser?.email || ""}
                        onChange={handleChange}
                      />
                    </FormControl>
                  </VStack>

                  {/* Module Management */}
                  <VStack w="100%" spacing={4} align="stretch">
                    <Text fontSize="lg" fontWeight="semibold">
                      {t('admin.manage_modules_button')}
                    </Text>
                    <Text fontSize="sm" color="gray.600">
                      {t('admin.select_modules')}
                    </Text>

                    {userModules.map((module) => (
                      <Box
                        key={module.id}
                        p={4}
                        border="1px"
                        borderColor="gray.200"
                        borderRadius="md"
                      >
                        <HStack justify="space-between">
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{module.label}</Text>
                            <Text fontSize="sm" color="gray.600">
                              {module.description}
                            </Text>
                          </VStack>
                          <Checkbox
                            isChecked={module.hasAccess}
                            onChange={() => handleModuleToggle(module.id)}
                            colorScheme="blue"
                          />
                        </HStack>
                      </Box>
                    ))}
                  </VStack>
                </VStack>
              </ModalBody>

              <ModalFooter gap="10px">
                <Button variant={"outline"} onClick={closeModal}>
                  {t('actions.cancel')}
                </Button>
                <Button colorScheme={"blue"} onClick={createNewUser} isLoading={isSaving}>
                  {t('actions.save')}
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        );
      case 'delete':
        return (
          <AlertDialog
            isOpen={isOpen}
            leastDestructiveRef={cancelRef}
            onClose={closeModal}
          >
            <AlertDialogOverlay>
              <AlertDialogContent>
                <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                  {t('admin.users_delete_title')}
                </AlertDialogHeader>

                <AlertDialogBody>
                  {t('actions.delete_confirm')}
                </AlertDialogBody>

                <AlertDialogFooter>
                  <Button ref={cancelRef} onClick={closeModal}>
                    {t('actions.cancel')}
                  </Button>
                  <Button colorScheme='red' isLoading={isDeleting} onClick={handleDeleteUser} ml={3}>
                    {t('actions.delete_confirm_button')}
                  </Button>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialogOverlay>
          </AlertDialog>
        );

    }
  }

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap="20px" w="100%" align="stretch">
        <PageHeading
          title={t('admin.users')}
          actions={[
            {
              label: t('actions.new_masculine') + ' ' + t('admin.users'),
              onClick: openNewUserModal,
              icon: <Icon as={MdPeople} />
            }
          ]}
        />

        <Panel>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            users && (
              <Table
                tableId="admin-users-table"
                columns={columns}
                data={users}
              />
            )
          )}
        </Panel>
      </VStack>
      {RenderModal()}
    </>
  );
};

export default UsersPage; 